import { Port } from '@ais-ui/api-spec/portserver';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { v4 as uuidv4 } from 'uuid';
import { createGeoJsonFromGeometry, Geometry } from '@ais-ui/utils';

export function createShapeDictionary(
  data: Record<
    string,
    string | number | Array<string | number> | Geometry | Geometry[]
  >,
  shapes: GeoJson[],
  adHoc = false
): Record<string, GeoJson> {
  const newShapeDictionary: Record<string, GeoJson> = {};
  const shapeMap = new Map<string, GeoJson>();

  for (const shape of shapes) {
    const normalizedId = normalizeShapeId(shape.properties?.id);
    if (!normalizedId) {
      continue;
    }
    if (!shapeMap.has(normalizedId)) {
      shapeMap.set(normalizedId, shape);
    }
  }

  for (const key in data) {
    const value = data[key];

    if (Array.isArray(value)) {
      if (adHoc) {
        const geometries = value as Geometry[];
        const features = geometries
          .map((geometry) => createGeoJsonFromGeometry(geometry))
          .filter((feature): feature is GeoJson => feature != null);

        if (features.length > 0) {
          newShapeDictionary[key] = createFeatureCollection(features);
        }
        continue;
      }

      const normalizedIds = value
        .map((id) => normalizeShapeId(id))
        .filter((id): id is string => !!id);

      if (normalizedIds.length === 0) {
        continue;
      }

      const features = normalizedIds
        .map((id) => shapeMap.get(id))
        .filter((shape): shape is GeoJson => !!shape);

      if (features.length > 0) {
        const featureCollection = createFeatureCollection(features);
        newShapeDictionary[key] = featureCollection;
      }

      continue;
    }

    if (adHoc) {
      const geometry = value as Geometry;
      newShapeDictionary[key] = createGeoJsonFromGeometry(geometry);
      continue;
    }

    const normalizedId = normalizeShapeId(value);
    if (!normalizedId) {
      continue;
    }

    const matchingShape = shapeMap.get(normalizedId);
    if (matchingShape) {
      newShapeDictionary[key] = matchingShape;
    }
  }

  return newShapeDictionary;
}

export function createPortDictionary(
  data: Record<string, string | string[]>,
  ports: Port[]
): Record<string, string[]> {
  const newPortDictionary: Record<string, string[]> = {};

  for (const key in data) {
    const value = data[key];
    // Convert value to array if it's a single string
    const portIds = Array.isArray(value) ? value : [value];

    // Filter valid ports and map to string IDs
    const matchingPorts = ports.reduce<string[]>((accumulator, port) => {
      const portId = port.id;
      if (portId == null) {
        return accumulator;
      }

      const portStringId = portId.toString();
      if (portIds.includes(portStringId)) {
        accumulator.push(portStringId);
      }

      return accumulator;
    }, []);

    if (matchingPorts.length > 0) {
      newPortDictionary[key] = matchingPorts;
    }
  }

  return newPortDictionary;
}

export function createWorldDictionary(
  data: Record<string, boolean>
): Record<string, boolean> {
  const newWorldDictionary: Record<string, boolean> = {};

  for (const key in data) {
    newWorldDictionary[key] = true;
  }

  return newWorldDictionary;
}

function createFeatureCollection(features: GeoJson[]): GeoJson {
  return {
    features: features,
    properties: {
      id: uuidv4(),
      name: 'temporary feature collection',
      owner: 'reports',
    },
    type: 'FeatureCollection',
  };
}

function normalizeShapeId(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'object') {
    const record = value as { properties?: { id?: unknown } };
    if (record.properties && 'id' in record.properties) {
      return normalizeShapeId(record.properties.id);
    }
  }

  return undefined;
}
